import { useNavigate } from "react-router-dom";
import { useAuth, type AuthContextType } from "@/hooks/useAuth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { Form } from "@/components/ReactHookForm/form";
import { Button } from "@/components/ui/button";
import type { CardMessageType } from "@/pages/Login.page";
import { CheckboxControl, TextControl } from "@/components/ReactHookForm/formInputs";

interface LoginFormProps {
	readonly setCardMessage: (isIt: CardMessageType) => void;
	readonly setIsResetting: ((isIt: boolean) => void) | null;
	readonly onSuccess: (result: AuthContextType["user"]) => void;
}
export function LoginForm(props: LoginFormProps) {
	const { login } = useAuth();
	const navigate = useNavigate();
	const form = useForm<FormType>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			username: "",
			password: "",
			rememberMe: false,
		},
	});
	const onSubmit: SubmitHandler<FormType> = async (data) => {
		props.setCardMessage(null);

		try {
			const tokenType = data.rememberMe ? "persistent" : "ephemeral";

			const result = await login(data.username, data.password, tokenType);
			if (result == null) {
				console.log("Login failed, no user returned");
				props.setCardMessage({
					msg: "Error al iniciar sesión. Por favor, verifica tus credenciales e inténtalo de nuevo.",
					type: "error",
				});
				return;
			}

			props.onSuccess(result);
		} catch (err) {
			console.error(err);

			const defaultErr =
				"Error al iniciar sesión. Por favor, verifica tus credenciales e inténtalo de nuevo. Si el problema persiste, contacta con el administrador.";
			const errString =
				typeof err == "object" &&
					err != null &&
					"message" in err &&
					typeof err.message === "string"
					? err.message
					: defaultErr;

			props.setCardMessage({
				msg: errString,
				type: "error",
			});
		}
	};
	const setModoReset = () => {
		if (props.setIsResetting == null) {
			void navigate("/login");
			return;
		}

		props.setCardMessage({
			msg: "Por favor, introduce tu dirección de correo electrónico para recibir un enlace de restablecimiento de contraseña.",
			type: "info",
		});
		props.setIsResetting(true);
	};

	return (
		<Form {...form}>
			<form
				onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
				className="space-y-8"
			>
				<TextControl
					control={form.control}
					name="username"
					label="Dirección de correo"
					placeholder="Dirección de correo"
					type="email"
					autoComplete="email"
				/>

				<TextControl
					control={form.control}
					name="password"
					label="Contraseña"
					placeholder="Contraseña"
					type="password"
					autoComplete="current-password"
				/>

				<div className="flex">
					<div className="flex-1 grid gap-1">
						<CheckboxControl
							control={form.control}
							name="rememberMe"
							label="Recuérdame"
						/>

						<a
							onClick={setModoReset}
							className="text-blue-500 hover:underline cursor-pointer"
						>
							¿Olvidaste tu contraseña?
						</a>
					</div>

					<div style={{ margin: "12px 0" }}>
						<Button type="submit">Iniciar Sesión</Button>
					</div>
				</div>
			</form>
		</Form>
	);
}

const FormSchema = z.object({
	username: z
		.email("Introduce un correo electrónico válido.")
		.transform((email) => email.toLowerCase()),
	password: z
		.string({
			error: "Por favor, introduce tus credenciales.",
		})
		.min(1, "Por favor, introduce tus credenciales."),
	rememberMe: z.boolean({}).optional(),
});
type FormType = z.infer<typeof FormSchema>;
